import { api, type ApiResponse } from '../client';

export interface CharityDashboardStats {
  totalValueCents: number;
  totalHours: number;
  totalDonations: number;
  pendingRequests: number;
  incomingDonationsValueCents: number;
  pendingConfirmationValueCents: number;
  pendingConfirmationCount?: number;
}

export interface PaginationInfo {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  from: number;
  to: number;
}

export interface DonationPayoutsResponse {
  success: boolean;
  data: DonationPayout[];
  paging: PaginationInfo;
  message?: string;
}

export interface DonationPayout {
  uuid: string;
  payout_id: string;
  payout_date: string;
  total_amount: string;
  payment_received?: number;
  l2g_fee_percentage?: number;
  l2g_fee_amount?: number;
  net_payment?: number;
  payment_reference?: string;
  total_donations: number;
  transaction_date: string;
  created_at: string;
  company: string;
  employer?: {
      uuid: string;
      name: string;
  };
  status: {
      uuid: string;
      name: string;
  };
  processed_by?: {
      uuid: string | null;
      name: string | null;
  };
  payment_method?: string;
  donation_breakdown?: {
      data: Array<{   
          charity: {
              uuid: string;
              name: string;
          };
          donation_request_status: {
              uuid: string;
              name: string;
          };
          employer: {
              uuid: string;
              name: string;
              employer_top_up: number;
          };
          user: {
              uuid: string;
              name: string;
          };
          user_status: {
              uuid: string;
              name: string;
          };
          leave_hours: number;
          leave_value: number;
          rejected_at: string | null;
          rejection_reason: string | null;
          total_donation: number;
          updated_at: string;
      }>;
      paging: {
          current_page: number;
          last_page: number;
          per_page: number;
          total: number;
      };
  };
  amount?: string;
  transaction_id?: string;
  charity?: {
      uuid: string;
      name: string;
  };
  donation_payout_status?: {
      uuid: string;
      name: string;
  };
  charity_marked_received_at?: string | null;
  received?: boolean;
}

export interface RecentDonation {
  donationId: string;
  employeeName: string;
  employerName: string;
  uuid: string;
  employeeInitials: string;
  user: {
    uuid: string;
    name: string;
  };
  employer?: {
    uuid: string;
    name: string;
  };
  hours: number;
  status: string;
  lifecycle_stage?: string;
  submittedAt: string;
  valueCents: number;
}

export interface CharityDashboardResponse {
  summary: CharityDashboardStats;
  recentPendingConfirmation: DonationPayout[];
  recentIncomingDonations: RecentDonation[];
  recentDonationsReceived: DonationPayout[];
}

export interface ApiSuccessResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

// API base URL
const API_BASE_URL = '/dashboard/charity';

export const CharityDashboardService = {
  /**
   * Get dashboard statistics for charity
   */
  getDashboardStats: async (): Promise<{
    stats?: CharityDashboardStats;
    recentPendingConfirmation?: DonationPayout[];
    recentIncomingDonations?: RecentDonation[];
    recentDonationsReceived?: DonationPayout[];
    success: boolean;
    error?: any;
  }> => {
    try {
      const response = await api.get<ApiSuccessResponse<CharityDashboardResponse>>(API_BASE_URL);
      
      if (response.data && response.data.success) {
        return {
          stats: response.data.data.summary,
          recentPendingConfirmation: response.data.data.recentPendingConfirmation || [],
          recentIncomingDonations: response.data.data.recentIncomingDonations || [],
          recentDonationsReceived: response.data.data.recentDonationsReceived || [],
          success: true
        };
      } else {
        return {
          success: false,
          error: response.data?.message || 'Failed to fetch dashboard statistics'
        };
      }
    } catch (error) {
      console.error('Error fetching dashboard statistics:', error);
      return {
        success: false,
        error
      };
    }
  },

  getPayouts: async (): Promise<{
    payouts?: DonationPayout[];
    paging?: PaginationInfo;
    success: boolean;
    error?: any;
}> => {
    try {
        const statsResult = await CharityDashboardService.getDashboardStats();
        if (statsResult.success) {
            const payouts = [
                ...(statsResult.recentPendingConfirmation || []),
                ...(statsResult.recentDonationsReceived || []),
            ];
            return {
                success: true,
                payouts
            };
        } else {
            return {
                success: false,
                error: 'Failed to retrieve payouts',
                payouts: []
            };
        }
    } catch (error) {
        console.error('Error fetching charity dashboard payouts:', error);
        return {
            success: false,
            error: 'An error occurred while fetching payouts',
            payouts: []
        };
    }
},
}; 